/**
 * Create audio (CONTEXT.md "Voice note"; ADR 0028): the chatter types what she says, it is
 * spoken in the Account voice and lands in the draft as a voice note, sent like a recorded
 * one. English lines get voice tags from the AI; any other language is spoken as typed.
 * Each one uses a voice note from the Account's quota, given back when no audio came back.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, type AccountVoice, type VoiceRegister } from '../api/client';
import { FeatherIcon } from '../components/FeatherIcon';
import { Spinner, BusyLabel } from '../components/Spinner';
import { MAX_CREATE_TEXT, voiceNoteFile, type CreatedVoiceNote } from './createdAudio';

interface Props {
  accountId: string;
  fanId: string;
  /** What is in the message field: offered as the line to speak. */
  initialText: string;
  /** `spoken` is the line it says. */
  onCreated: (note: CreatedVoiceNote, spoken: string) => void;
  onClose: () => void;
}

export function CreateAudioPanel({ accountId, fanId, initialText, onCreated, onClose }: Props) {
  const { t } = useTranslation('chat');
  const [voice, setVoice] = useState<AccountVoice | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [text, setText] = useState(initialText);
  const [register, setRegister] = useState<VoiceRegister>('normal');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let live = true;
    api.getAccountVoice(accountId).then(
      (v) => live && setVoice(v),
      (e: unknown) => live && setLoadError((e as Error).message),
    );
    field.current?.focus();
    return () => {
      live = false;
    };
  }, [accountId]);

  const blocker = !voice
    ? null
    : !voice.voice
      ? t('createAudio.noVoice')
      : voice.voice.status !== 'ready'
        ? t('createAudio.notReady')
        : voice.quota.remaining <= 0
          ? t('createAudio.quota')
          : !voice.voice_service_ready
            ? t('createAudio.unavailable')
            : null;
  const tooLong = text.length > MAX_CREATE_TEXT;
  const canCreate = !!voice && !blocker && !busy && !!text.trim() && !tooLong;

  async function create() {
    if (!canCreate) return;
    setBusy(true);
    setError(null);
    try {
      const made = await api.createAudio(accountId, { text, fan_id: fanId, voice_register: register });
      setVoice((v) => v && { ...v, quota: made.quota });
      onCreated(voiceNoteFile(made), text);
    } catch (e) {
      const msg = (e as Error).message;
      setError(msg === 'REQUEST_TIMEOUT' ? t('createAudio.timeout') : msg || t('createAudio.failed'));
      // A refused or failed one may or may not have been charged: show the real count.
      api.getAccountVoice(accountId).then(setVoice, () => undefined);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rc-pop rc-audio-pop" role="dialog" aria-label={t('createAudio.title')}>
      <div className="rc-pop-head">
        <FeatherIcon name="volume-2" size={14} />
        <span className="rc-audio-title">{t('createAudio.title')}</span>
        <span className="rc-audio-beta">{t('createAudio.beta')}</span>
        <span style={{ flex: 1 }} />
        {voice && (
          <span className="rc-muted rc-audio-meta">
            {voice.voice ? t('createAudio.voice', { name: voice.voice.label }) : null}
            {voice.voice && ' · '}
            {t('createAudio.left', { n: voice.quota.remaining, total: voice.quota.allowed })}
          </span>
        )}
        <button type="button" className="rc-pop-close" title={t('common.close')} onClick={onClose}>
          <FeatherIcon name="x" size={14} />
        </button>
      </div>
      {!voice && !loadError && <div className="rc-pop-empty"><Spinner size={16} label={t('createAudio.loading')} /></div>}
      {loadError && <div className="rc-pop-empty">{loadError}</div>}
      {voice && blocker && (
        <div className="rc-pop-empty">
          {blocker}
          {!voice.voice && (
            <>
              {' '}
              <Link to="/audio" className="rc-audio-link">
                {t('createAudio.setVoice')}
              </Link>
            </>
          )}
        </div>
      )}
      {voice && !blocker && (
        <>
          <div className="rc-audio-register" role="radiogroup" aria-label={t('createAudio.register')}>
            {(['normal', 'hot'] as const).map((r) => (
              <button key={r} type="button" role="radio" aria-checked={register === r} className={register === r ? 'is-on' : ''} onClick={() => setRegister(r)}>
                {t(`createAudio.${r}`)}
              </button>
            ))}
          </div>
          <textarea
            ref={field}
            className="rc-pop-textarea rc-audio-text"
            rows={3}
            value={text}
            placeholder={t('createAudio.placeholder')}
            aria-label={t('createAudio.text')}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void create();
            }}
          />
          <div className="rc-audio-foot">
            <span className={tooLong || error ? 'rc-audio-err' : 'rc-muted'}>{tooLong ? t('createAudio.tooLong', { max: MAX_CREATE_TEXT }) : error ?? t('createAudio.hint')}</span>
            <button type="button" className="rc-btn rc-btn--primary" disabled={!canCreate} onClick={() => void create()}>
              {busy ? <Spinner size={12} label={t('createAudio.creating')} /> : <FeatherIcon name="mic" size={13} />}
              <BusyLabel busy={busy} label={t('createAudio.creating')}>{t('createAudio.create')}</BusyLabel>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
